import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Heart,
  PaperPlaneTilt,
  BookmarkSimple,
  SealCheck,
  CaretLeft,
} from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import RightRail from "@/components/RightRail";
import { posts, commentsByPost } from "@/data/mockData";

export default function PostDetail() {
  const { id } = useParams();
  const post = posts.find((p) => p.id === id) || posts[0];
  const comments = commentsByPost[post.id] || commentsByPost.p1;
  const [liked, setLiked] = useState(post.liked);
  const [text, setText] = useState("");

  return (
    <Layout right={<RightRail />}>
      <div data-testid="post-page">
        <header className="sticky top-0 z-20 bg-[#050505]/90 backdrop-blur-lg border-b border-[#27272A] px-4 md:px-6 py-4 flex items-center gap-4">
          <Link
            to="/"
            className="h-9 w-9 inline-flex items-center justify-center border border-[#27272A] hover:border-[#FF3E00]"
          >
            <CaretLeft size={16} />
          </Link>
          <div>
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em]">
              POST · {post.id.toUpperCase()}
            </div>
            <div className="font-display font-bold">{post.artist.name}</div>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12">
          {/* Media */}
          {post.image ? (
            <div className="lg:col-span-7 bg-black border-b lg:border-b-0 lg:border-r border-[#27272A]">
              <div className="aspect-square lg:aspect-auto lg:min-h-[640px] relative">
                <img
                  src={post.image}
                  alt="post"
                  className="absolute inset-0 w-full h-full object-contain"
                />
              </div>
            </div>
          ) : (
            <div className="lg:col-span-7 bg-[#0a0a0a] p-8 md:p-16 border-b lg:border-b-0 lg:border-r border-[#27272A] flex items-center">
              <p className="font-display text-3xl md:text-5xl font-medium leading-[1.2]">
                {post.caption}
              </p>
            </div>
          )}

          {/* Sidebar: meta + comments */}
          <aside className="lg:col-span-5 flex flex-col">
            {/* Artist */}
            <Link
              to={`/artista/${post.artist.slug}`}
              className="flex items-center gap-3 p-4 md:p-5 border-b border-[#27272A]"
            >
              <img
                src={post.artist.avatar}
                alt={post.artist.name}
                className="h-12 w-12 object-cover border border-[#27272A]"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-display font-bold">{post.artist.name}</span>
                  {post.artist.verified ? (
                    <SealCheck size={13} weight="fill" className="text-[#FF3E00]" />
                  ) : null}
                </div>
                <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                  @{post.artist.handle} · {post.time.toUpperCase()}
                </div>
              </div>
              <button className="btn-brutal font-mono uppercase text-[10px] tracking-[0.2em] bg-[#FF3E00] text-white px-3 py-1.5">
                + Seguir
              </button>
            </Link>

            {/* Caption */}
            {post.image ? (
              <div className="p-4 md:p-5 border-b border-[#27272A]">
                <p className="text-[15px] leading-[1.6]">{post.caption}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {post.tags.map((t) => (
                    <span
                      key={t}
                      className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#FF3E00]"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Comments */}
            <div className="flex-1 overflow-auto">
              <div className="px-4 md:px-5 py-3 border-b border-[#27272A]">
                <span className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.25em]">
                  {comments.length} COMENTARIOS
                </span>
              </div>
              <ul>
                {comments.map((c) => (
                  <li
                    key={c.id}
                    className="flex gap-3 p-4 md:p-5 border-b border-[#27272A]"
                  >
                    <img
                      src={c.artist.avatar}
                      alt={c.artist.name}
                      className="h-9 w-9 object-cover border border-[#27272A] shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-display font-bold text-sm">
                          {c.artist.name}
                        </span>
                        <span className="font-mono text-[10px] text-[#A1A1AA]">
                          {c.time}
                        </span>
                      </div>
                      <p className="text-[14px] text-[#E5E5E5] mt-1">{c.text}</p>
                      <div className="mt-2 flex items-center gap-4 font-mono text-[10px] text-[#A1A1AA]">
                        <button className="hover:text-[#FF3E00]">
                          ♥ {c.likes}
                        </button>
                        <button className="hover:text-white uppercase tracking-wider">
                          Responder
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Actions bar */}
            <div className="border-t border-[#27272A]">
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-1">
                  <button
                    data-testid="post-like"
                    onClick={() => setLiked((v) => !v)}
                    className={`p-2 ${liked ? "text-[#FF3E00]" : "text-[#A1A1AA] hover:text-white"}`}
                  >
                    <Heart size={22} weight={liked ? "fill" : "regular"} />
                  </button>
                  <button className="p-2 text-[#A1A1AA] hover:text-white">
                    <PaperPlaneTilt size={22} />
                  </button>
                </div>
                <button className="p-2 text-[#A1A1AA] hover:text-white">
                  <BookmarkSimple size={22} />
                </button>
              </div>
              <div className="px-4 pb-2">
                <div className="font-display font-bold">
                  {post.likes.toLocaleString("es")} me gusta
                </div>
              </div>

              {/* Compose */}
              <div className="flex items-center gap-2 border-t border-[#27272A] p-3">
                <input
                  data-testid="comment-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Escribe un comentario..."
                  className="flex-1 bg-transparent focus:outline-none text-sm px-2 font-mono"
                />
                <button
                  data-testid="comment-submit"
                  disabled={!text.trim()}
                  className={`font-mono uppercase text-[10px] tracking-[0.2em] px-3 py-2 ${
                    text.trim()
                      ? "bg-[#FF3E00] text-white"
                      : "bg-[#121212] text-[#525252] cursor-not-allowed"
                  }`}
                >
                  Enviar
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}
